<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      leave-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <!-- Backdrop -->
      <div
        v-if="isOpen"
        class="fixed inset-0 flex items-center justify-center p-4 bg-black/50"
        :style="{ zIndex: zIndex }"
        @click="handleBackdropClick"
        @keydown.esc="handleEscapeKey"
      >
        <Transition
          enter-active-class="transition-all duration-300"
          leave-active-class="transition-all duration-300"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <!-- Modal Content -->
          <div
            v-if="isOpen"
            class="relative w-full mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
            :class="[sizeClasses, responsiveClasses]"
            role="dialog"
            :aria-labelledby="title ? 'modal-title' : undefined"
            :aria-describedby="description ? 'modal-description' : undefined"
            @click.stop
          >
            <!-- Header -->
            <div
              v-if="title || $slots.header"
              class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700"
            >
              <div class="flex-1">
                <h3
                  v-if="title"
                  id="modal-title"
                  class="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {{ title }}
                </h3>
                <slot name="header" />
              </div>
              <button
                v-if="showCloseButton"
                @click="close"
                class="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close modal"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div class="p-6" :class="bodyClasses">
              <p
                v-if="description"
                id="modal-description"
                class="text-sm text-gray-600 dark:text-gray-400 mb-4"
              >
                {{ description }}
              </p>
              <slot />
            </div>

            <!-- Footer -->
            <div
              v-if="$slots.footer"
              class="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700"
            >
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted, onMounted, type PropType, type ComputedRef } from 'vue'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  size: {
    type: String as PropType<ModalSize>,
    default: 'md',
    validator: (value: string) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value),
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  closeOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  preventBodyScroll: {
    type: Boolean,
    default: true,
  },
  zIndex: {
    type: Number,
    default: 50,
  },
})

const emit = defineEmits<{
  (_e: 'close'): void
  (_e: 'opened'): void
  (_e: 'closed'): void
}>()

const sizeClasses: ComputedRef<string> = computed(() => {
  const sizes: Record<ModalSize, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full',
  }
  return sizes[props.size]
})

const responsiveClasses: ComputedRef<string> = computed(() => {
  const responsive: Record<ModalSize, string> = {
    sm: 'w-full',
    md: 'w-full sm:max-w-md',
    lg: 'w-full sm:max-w-lg',
    xl: 'w-full sm:max-w-xl',
    full: 'w-full',
  }
  return responsive[props.size]
})

const bodyClasses: ComputedRef<string> = computed(() => {
  return props.preventBodyScroll ? 'max-h-[80vh] overflow-y-auto' : ''
})

function close(): void {
  emit('close')
}

function handleBackdropClick(): void {
  if (props.closeOnBackdropClick) {
    close()
  }
}

function handleEscapeKey(): void {
  close()
}

// Handle body scroll prevention and focus management
watch(
  () => props.isOpen,
  (isOpen) => {
    if (props.preventBodyScroll) {
      if (isOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
    if (isOpen) {
      emit('opened')
    } else {
      emit('closed')
    }
  },
)

// Focus management
onMounted(() => {
  if (props.isOpen) {
    // Focus the modal when it opens
    const modalElement = document.querySelector('[role="dialog"]') as HTMLElement | null
    if (modalElement) {
      modalElement.focus()
    }
  }
})

// Cleanup on unmount
onUnmounted(() => {
  if (props.preventBodyScroll) {
    document.body.style.overflow = ''
  }
})
</script>
